// Cart utility functions stored in client-side localStorage

// NOTE: The cart is now stored exclusively in localStorage to keep it fully client-side.
// We keep a small migration step that reads an existing `diamond-cart` cookie once (if present)
// and moves it into localStorage, then deletes the cookie so it is no longer sent with requests.

const CART_KEY = 'diamond-cart';

// TODO Refactor cart to be a hook
export interface CartItem {
  diamondId: string;
  price: number;
  discount: string | number;
  /** Original marked-up price */
  markupPrice?: number;
  finalPrice: number;
  carats: number;
  shape: string;
  cut: string;
  color: string;
  clarity: string;
  lab: string;
  labgrown: boolean;
  /** Optional thumbnail image URL */
  image?: string;
  /** Shopify variant ID when the item represents a specific product variant (primarily for rings) */
  variantId?: string;
  /** Shopify product ID (GID) for rings so we can navigate to the PDP */
  productId?: string;
  addedAt: string;
  /**
   * Optional fields for non-diamond items (e.g. rings)
   */
  itemType?: 'diamond' | 'ring';
  /** Human-readable product name for rings */
  productName?: string;
  /** Metal selection for rings (e.g. 14KT) */
  metal?: string;
  /** Stone type (Natural / Lab-Grown) for rings */
  stoneType?: string;
  /** All center-stone shapes this setting supports (for ring items) */
  supportedShapes?: string[];
  /** Additional ring specifications */
  bandWidthMm?: number;
  sideStonesCount?: number;
  sideStonesWeightCttw?: number;
  sideStoneColour?: string;
  sideStoneClarity?: string;
}

export interface Diamond {
  diamondId: string;
  price: number;
  discount: string | number;
  carats: number;
  fullShape?: string;
  shape: string;
  cut: string;
  col: string;
  clar: string;
  lab: string;
  labgrown: boolean;
  markupPrice?: number;
  /** Shopify variant ID when the item represents a specific product variant */
  variantId?: string;
  /** Shopify product ID (GID) */
  productId?: string;
  /** Optional properties when the item represents a ring */
  itemType?: 'diamond' | 'ring';
  productName?: string;
  metal?: string;
  stoneType?: string;
  /** All center-stone shapes this setting supports (for ring items) */
  supportedShapes?: string[];
  /** Additional ring specifications */
  bandWidthMm?: number;
  sideStonesCount?: number;
  sideStonesWeightCttw?: number;
  sideStoneColour?: string;
  sideStoneClarity?: string;
}

export const getCart = (): CartItem[] => {
  // During SSR there is no cart – it is strictly a client asset
  if (typeof window === 'undefined') return [];

  try {
    const stored = window.localStorage.getItem(CART_KEY);
    if (stored) return JSON.parse(stored);

    return [];
  } catch (error) {
    console.error('Error reading cart:', error);
    return [];
  }
};

export const setCart = (cart: CartItem[]): void => {
  if (typeof window === 'undefined') return; // Ignore during SSR

  try {
    window.localStorage.setItem(CART_KEY, JSON.stringify(cart));
  } catch (error) {
    console.error('Error saving cart:', error);
  }
};

export const addToCart = (item: Diamond): boolean => {
  try {
    let cart = getCart();

    const isRing = item.itemType === 'ring';

    // Remove existing item of the same type (diamond vs ring)
    cart = cart.filter((c) => c.itemType !== (isRing ? 'ring' : 'diamond'));

    // Build cart item representation
    const cartItem: CartItem = {
      diamondId: item.diamondId,
      price: item.price,
      discount: item.discount,
      markupPrice: (item as any).markupPrice,
      // Use markupPrice when available as the primary price shown in cart
      finalPrice: (item as any).markupPrice ?? item.price,
      carats: item.carats,
      shape: item.shape,
      cut: item.cut,
      color: item.col,
      clarity: item.clar,
      lab: item.lab,
      // Determine lab-grown flag – use `labgrown` when provided, otherwise fall back to `labgrownType`
      labgrown:
        typeof (item as any).labgrown === 'boolean'
          ? (item as any).labgrown
          : Boolean((item as any).labgrownType),
      image: (item as any).image,
      addedAt: new Date().toISOString(),
      itemType: item.itemType ?? 'diamond',
      productName: item.productName,
      metal: item.metal,
      stoneType: item.stoneType,
      supportedShapes: (item as any).supportedShapes,
      // Ring-specific specs
      bandWidthMm: (item as any).bandWidthMm,
      sideStonesCount: (item as any).sideStonesCount,
      sideStonesWeightCttw: (item as any).sideStonesWeightCttw,
      sideStoneColour: (item as any).sideStoneColour,
      sideStoneClarity: (item as any).sideStoneClarity,
      // Preserve identifiers for accurate restoration and navigation
      ...(item.variantId ? { variantId: item.variantId } : {}),
      ...(item.productId ? { productId: item.productId } : {}),
    };

    cart.push(cartItem);
    setCart(cart);
    return true;
  } catch (error) {
    console.error('Error adding to cart:', error);
    return false;
  }
};

export const removeFromCart = (diamondId: string): boolean => {
  try {
    const cart = getCart();
    const updatedCart = cart.filter((item) => item.diamondId !== diamondId);
    setCart(updatedCart);
    return true;
  } catch (error) {
    console.error('Error removing from cart:', error);
    return false;
  }
};

export const clearCart = (): boolean => {
  try {
    setCart([]);
    return true;
  } catch (error) {
    console.error('Error clearing cart:', error);
    return false;
  }
};

export const getCartTotal = (): number => {
  const cart = getCart();
  return cart.reduce((total, item) => total + item.finalPrice, 0);
};

export const getCartCount = (): number => {
  return getCart().length;
};
